export enum Role {
  Buyer = "buyer",
  Seller = "seller",
}

export enum Stage {
  Negotiation = "negotiation",
  Counter = "counter",
  Accepted = "accepted",
  Rejected = "rejected",
  SoldOut = "soldout",
  Handover = "handover",
  Ended = "ended",
}

export type TemplateId =
  // Buyer
  | "B_MAKE_OFFER"
  | "B_ACCEPT"
  | "B_COUNTER"
  | "B_CEILING"
  | "B_WITHDRAW" // <--- Added: Buyer declines
  | "B_ACK_END"
  | "B_SHARE_CONTACT"
  | "B_ACK_CONTACT"
  // Seller
  | "S_ACCEPT"
  | "S_COUNTER"
  | "S_FIRM_PRICE"
  | "S_SOLD_OUT"
  | "S_WITHDRAW"
  | "S_ACK_END"
  | "S_SHARE_CONTACT"
  | "S_PRODUCT_DELETED"
  | "S_PRODUCT_SOLD_OUT";

export interface Template {
  id: TemplateId;
  role: Role;
  stage: Stage;
  text: string;
  shortForm?: string;
  params?: string[];
  next: TemplateId[];
  transition?: Stage;
  ends?: boolean;
  note?: string;
  /** If true, show confirmation popup even without params (displays note and requires user confirmation) */
  requiresConfirmation?: boolean;
}

// Templates library
export const TEMPLATES: Template[] = [
  // --- SELLER MESSAGES ---

  {
    id: "S_ACCEPT",
    role: Role.Seller,
    stage: Stage.Negotiation,
    text: "Deal! I agree to sell at {offerPrice}. Can you share your contact to proceed further?",
    shortForm: "Accept deal",
    params: ["offerPrice"],
    requiresConfirmation: true,
    note: "This confirms the sale. You cannot change the price after this.",
    next: ["B_SHARE_CONTACT"],
    transition: Stage.Accepted,
  },
  {
    id: "S_COUNTER",
    role: Role.Seller,
    stage: Stage.Negotiation,
    text: "That's a bit low. I can do {counterPrice}.",
    shortForm: "Suggest different price",
    params: ["counterPrice"],
    note: "The buyer can accept this, negotiate again, or decline if it's too high.",
    next: ["B_ACCEPT", "B_WITHDRAW", "B_COUNTER", "B_CEILING"],
    transition: Stage.Counter,
  },
  {
    id: "S_FIRM_PRICE",
    role: Role.Seller,
    stage: Stage.Negotiation,
    text: "The lowest I can go is {priceFloor}. I can't lower it further.",
    shortForm: "State lowest price",
    params: ["priceFloor"],
    note: "This is an ultimatum. The buyer CANNOT negotiate further; they must accept or walk away.",
    next: ["B_ACCEPT", "B_WITHDRAW"],
    transition: Stage.Counter,
    // requiresConfirmation: true,
  },
  {
    id: "S_SOLD_OUT",
    role: Role.Seller,
    stage: Stage.Negotiation,
    text: "Sorry, this item is already sold.",
    shortForm: "Mark as Sold",
    note: "This will immediately end the conversation.",
    next: ["B_ACK_END"],
    transition: Stage.SoldOut,
    requiresConfirmation: true,
  },
  {
    id: "S_WITHDRAW",
    role: Role.Seller,
    stage: Stage.Negotiation,
    text: "No thanks, I'm not interested in selling at that price.",
    shortForm: "Decline offer",
    note: "You are rejecting the offer. The conversation will end.",
    next: ["B_ACK_END"],
    transition: Stage.Rejected,
    requiresConfirmation: true,
  },
  {
    id: "S_ACK_END",
    role: Role.Seller,
    stage: Stage.Rejected,
    text: "Okay, no problem. Thanks.",
    shortForm: "Okay, thanks",
    note: "Polite closing message.",
    next: [],
    transition: Stage.Ended,
    ends: true,
    requiresConfirmation: true,
  },
  {
    id: "S_SHARE_CONTACT",
    role: Role.Seller,
    stage: Stage.Accepted,
    text: "Sure! Click here to connect with me in whatsapp {phoneNumber}",
    shortForm: "Share contact",
    params: ["phoneNumber"],
    note: "This shares your whatsapp number with the buyer to communicate about meetup/payment/delivery.",
    next: ["B_ACK_CONTACT"],
    transition: Stage.Handover,
    requiresConfirmation: true,
  },
  {
    id: "S_ACK_CONTACT",
    role: Role.Seller,
    stage: Stage.Handover,
    text: "Great! I will connect with you",
    shortForm: "Acknowledge Contact",
    note: "Acknowledge receipt of contact.",
    next: [],
    transition: Stage.Ended,
    ends: true,
  },
  {
    id: "S_PRODUCT_DELETED",
    role: Role.Seller,
    stage: Stage.Negotiation,
    text: "This listing has been deleted and is no longer available.",
    note: "The seller has permanently deleted this product. The conversation is now closed.",
    next: [],
    transition: Stage.Ended,
    ends: true,
  },
  {
    id: "S_PRODUCT_SOLD_OUT",
    role: Role.Seller,
    stage: Stage.Negotiation,
    text: "This product is now sold out. The seller is no longer accepting offers for this item.",
    note: "The product is out of stock. The conversation is now closed.",
    next: [],
    transition: Stage.Ended,
    ends: true,
  },
  {
    id: "S_PRODUCT_DELETED",
    role: Role.Seller,
    stage: Stage.Negotiation,
    text: "This listing has been deleted and is no longer available.",
    note: "The seller has permanently deleted this product. The conversation is now closed.",
    next: [],
    transition: Stage.Ended,
    ends: true,
  },
  {
    id: "S_PRODUCT_SOLD_OUT",
    role: Role.Seller,
    stage: Stage.Negotiation,
    text: "This product is now sold out. The seller is no longer accepting offers for this item.",
    note: "The product is out of stock. The conversation is now closed.",
    next: [],
    transition: Stage.Ended,
    ends: true,
  },

  // --- BUYER MESSAGES ---

  {
    id: "B_MAKE_OFFER",
    role: Role.Buyer,
    stage: Stage.Negotiation,
    text: "I want to offer {offerPrice} for this.",
    params: [],
    requiresConfirmation: true,
    note: "The seller can accept, reject, or suggest a different price.",
    next: ["S_ACCEPT", "S_COUNTER", "S_FIRM_PRICE", "S_WITHDRAW"],
    transition: Stage.Accepted,
  },
  {
    id: "B_COUNTER",
    role: Role.Buyer,
    stage: Stage.Counter,
    text: "Would you accept {counterPrice}?",
    shortForm: "Offer different price",
    params: ["counterPrice"],
    note: "The seller might accept, or they might counter you again.",
    next: ["S_COUNTER", "S_FIRM_PRICE", "S_ACCEPT"],
    transition: Stage.Negotiation,
  },
  {
    id: "B_CEILING",
    role: Role.Buyer,
    stage: Stage.Counter,
    text: "I can't pay more than {priceFloor}.That is strictly my strict budget",
    shortForm: "State max budget",
    params: ["priceFloor"],
    note: "This is your final offer. The seller cannot negotiate further.",
    next: ["S_ACCEPT", "S_WITHDRAW"],
    transition: Stage.Negotiation,
  },
  {
    id: "B_ACCEPT",
    role: Role.Buyer,
    stage: Stage.Counter,
    text: "Okay, I agree to {offerPrice}. Can you share your contact for further details?",
    shortForm: "Accept price",
    params: ["offerPrice"],
    note: "You are committing to buy at this price. Next step is exchanging contact info.",
    next: ["S_SHARE_CONTACT"],
    transition: Stage.Accepted,
    requiresConfirmation: true,
  },
  {
    id: "B_WITHDRAW",
    role: Role.Buyer,
    stage: Stage.Counter,
    text: "That's a bit too high for me. I'll pass.",
    shortForm: "Decline & End",
    note: "You are cancelling the negotiation. The conversation will end.",
    next: ["S_ACK_END"],
    transition: Stage.Rejected,
    requiresConfirmation: true,
  },
  {
    id: "B_ACK_END",
    role: Role.Buyer,
    stage: Stage.Rejected,
    text: "Okay, understood. Thanks anyway.",
    shortForm: "Okay, thanks",
    note: "Polite closing message.",
    next: [],
    transition: Stage.Ended,
    ends: true,
    requiresConfirmation: true,
  },
  {
    id: "B_SHARE_CONTACT",
    role: Role.Buyer,
    stage: Stage.Accepted,
    text: "Sure! Click here to connect with me in whatsapp {phoneNumber}",
    shortForm: "Share contact",
    note: "This shares your whatsapp number with the seller to communicate about meetup/payment/delivery.",
    params: ["phoneNumber"],
    next: ["S_ACK_CONTACT"],
    transition: Stage.Handover,
    requiresConfirmation: true,
  },
  {
    id: "B_ACK_CONTACT",
    role: Role.Buyer,
    stage: Stage.Handover,
    text: "Great! I will connect with you",
    shortForm: "Acknowledge Contact",
    note: "Acknowledge receipt of contact.",
    next: [],
    transition: Stage.Ended,
    ends: true,
  },
];

const templateById = new Map<TemplateId, Template>(
  TEMPLATES.map((t) => [t.id, t]),
);

export interface SuggestContext {
  role: Role;
  stage: Stage;
  lastTemplateId?: TemplateId;
}

export function getSuggestions(ctx: SuggestContext): Template[] {
  if (ctx.lastTemplateId) {
    const last = templateById.get(ctx.lastTemplateId);
    if (last) {
      const next = last.next.map((id) => templateById.get(id)!).filter(Boolean);
      return next.filter((t) => t.role === ctx.role);
    }
  }
  return TEMPLATES.filter((t) => t.role === ctx.role && t.stage === ctx.stage);
}

export function getTemplateById(id: TemplateId): Template | undefined {
  return templateById.get(id);
}

export function renderTemplate(
  text: string,
  params: Record<string, null | number | string>,
): string {
  return text.replace(/\{(\w+)\}/g, (_, k) =>
    (params[k] ?? `{${k}}`).toString(),
  );
}